import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { supabase } from "@/services/supabase";
import { useSessionStore } from "./sessionStore";

interface CompanyUser {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    company_id: string;
    active: boolean;
    password_reset_required: boolean;
}

interface CompanyUserStore {
    companyUser: CompanyUser | null;
    isCompanyUserLoading: boolean;
    companyUserError: string | null;
    invitationStatus?:
        | "checking"
        | "processing"
        | "completed"
        | "not_found"
        | "error";
    requiresPasswordReset: boolean;

    fetchCompanyUser: () => Promise<void>;
    processInvitation: () => Promise<void>;
    clearPasswordResetRequired: () => Promise<void>;
    clearCompanyUserData: () => void;
    setCompanyUser: (companyUser: CompanyUser | null) => void;

    isFullyAuthenticated: () => boolean;
    hasCompanyUser: () => boolean;
}

async function loadOwnCompanyUser(userId: string) {
    return await supabase
        .from("company_users")
        .select("*")
        .eq("id", userId)
        .eq("active", true)
        .maybeSingle();
}

function isAlreadyAcceptedPayload(
    data: unknown,
    error: unknown,
): boolean {
    if (data && typeof data === "object" && "already_accepted" in data) {
        return Boolean(
            (data as { already_accepted?: boolean }).already_accepted,
        );
    }

    const message =
        error && typeof error === "object" && "message" in error
            ? String((error as { message?: string }).message ?? "")
            : "";

    return (
        message.toLowerCase().includes("already been accepted") ||
        message.toLowerCase().includes("profile already exists") ||
        message.toLowerCase().includes("company user already exists")
    );
}

export const useCompanyUserStore = create<CompanyUserStore>()(
    devtools(
        (set, get) => ({
            companyUser: null,
            isCompanyUserLoading: false,
            companyUserError: null,
            invitationStatus: undefined,
            requiresPasswordReset: false,

            fetchCompanyUser: async () => {
                const session = useSessionStore.getState().session;
                if (!session?.user) {
                    set(
                        { companyUserError: "No active session" },
                        false,
                        "fetchCompanyUser/noSession",
                    );
                    return;
                }

                const currentState = get();
                if (currentState.isCompanyUserLoading) {
                    return;
                }

                set(
                    { isCompanyUserLoading: true, companyUserError: null },
                    false,
                    "fetchCompanyUser/start",
                );

                try {
                    const { data: companyUser, error } =
                        await loadOwnCompanyUser(session.user.id);

                    if (companyUser && !error) {
                        set(
                            {
                                companyUser,
                                isCompanyUserLoading: false,
                                companyUserError: null,
                                requiresPasswordReset: Boolean(
                                    companyUser.password_reset_required,
                                ),
                            },
                            false,
                            "fetchCompanyUser/success",
                        );
                        return;
                    }

                    await get().processInvitation();
                } catch (error) {
                    const errorMessage = error instanceof Error
                        ? error.message
                        : "Unknown error";
                    set(
                        {
                            companyUser: null,
                            isCompanyUserLoading: false,
                            companyUserError: errorMessage,
                        },
                        false,
                        "fetchCompanyUser/error",
                    );
                    console.error("Error fetching company user:", error);
                }
            },

            processInvitation: async () => {
                const session = useSessionStore.getState().session;
                if (!session?.user) return;

                set(
                    { invitationStatus: "checking" },
                    false,
                    "processInvitation/checking",
                );

                try {
                    const { data, error: acceptError } = await supabase.functions
                        .invoke("accept-invite", {
                            body: { user_id: session.user.id },
                        });

                    const alreadyAccepted = isAlreadyAcceptedPayload(
                        data,
                        acceptError,
                    );

                    if (acceptError && !alreadyAccepted) {
                        let message = acceptError.message ?? "";
                        if (
                            typeof acceptError === "object" &&
                            "context" in acceptError &&
                            (acceptError as { context: unknown }).context instanceof
                                Response
                        ) {
                            try {
                                const body = await (
                                    (acceptError as { context: Response }).context
                                ).json() as { error?: string };
                                if (body?.error) message = body.error;
                            } catch {
                                /* ignore */
                            }
                        }

                        const status = message.toLowerCase().includes(
                                "not found",
                            )
                            ? "not_found"
                            : "error";
                        set(
                            {
                                invitationStatus: status,
                                isCompanyUserLoading: false,
                                companyUserError:
                                    message || "Invitation failed",
                            },
                            false,
                            "processInvitation/error",
                        );
                        return;
                    }

                    set(
                        { invitationStatus: "processing" },
                        false,
                        "processInvitation/processing",
                    );

                    const { data: newCompanyUser, error: newCompanyUserError } =
                        await loadOwnCompanyUser(session.user.id);

                    if (newCompanyUser && !newCompanyUserError) {
                        set(
                            {
                                companyUser: newCompanyUser,
                                isCompanyUserLoading: false,
                                companyUserError: null,
                                invitationStatus: "completed",
                                requiresPasswordReset: Boolean(
                                    newCompanyUser.password_reset_required,
                                ),
                            },
                            false,
                            "processInvitation/completed",
                        );
                    } else {
                        set(
                            {
                                invitationStatus: "error",
                                isCompanyUserLoading: false,
                                companyUserError:
                                    "Failed to fetch newly created company user",
                            },
                            false,
                            "processInvitation/companyUserError",
                        );
                    }
                } catch (error) {
                    console.error("Error processing invitation:", error);
                    set(
                        {
                            invitationStatus: "error",
                            isCompanyUserLoading: false,
                            companyUserError: error instanceof Error
                                ? error.message
                                : "Unknown error",
                        },
                        false,
                        "processInvitation/error",
                    );
                }
            },

            clearPasswordResetRequired: async () => {
                const session = useSessionStore.getState().session;
                const companyUser = get().companyUser;
                if (!session?.user || !companyUser) return;

                const { error } = await supabase
                    .from("company_users")
                    .update({ password_reset_required: false })
                    .eq("id", session.user.id);

                if (error) {
                    console.error(
                        "Failed to clear password_reset_required:",
                        error,
                    );
                    throw error;
                }

                set(
                    {
                        companyUser: {
                            ...companyUser,
                            password_reset_required: false,
                        },
                        requiresPasswordReset: false,
                    },
                    false,
                    "clearPasswordResetRequired",
                );
            },

            setCompanyUser: (companyUser) => {
                set({ companyUser }, false, "setCompanyUser");
            },

            clearCompanyUserData: () =>
                set(
                    {
                        companyUser: null,
                        isCompanyUserLoading: false,
                        companyUserError: null,
                        invitationStatus: undefined,
                        requiresPasswordReset: false,
                    },
                    false,
                    "clearCompanyUserData",
                ),

            isFullyAuthenticated: () => {
                const session = useSessionStore.getState().session;
                return session !== null && get().companyUser !== null;
            },
            hasCompanyUser: () => get().companyUser !== null,
        }),
        {
            name: "company-user-store",
        },
    ),
);

export const useCompanyUser = () =>
    useCompanyUserStore((state) => state.companyUser);
export const useCompanyUserLoading = () =>
    useCompanyUserStore((state) => state.isCompanyUserLoading);
export const useInvitationStatus = () =>
    useCompanyUserStore((state) => state.invitationStatus);
export const useRequiresPasswordReset = () =>
    useCompanyUserStore((state) => state.requiresPasswordReset);
export const useIsFullyAuthenticated = () =>
    useCompanyUserStore((state) => state.isFullyAuthenticated());
export const useFetchCompanyUser = () =>
    useCompanyUserStore((state) => state.fetchCompanyUser);
export const useSetCompanyUser = () =>
    useCompanyUserStore((state) => state.setCompanyUser);
export const useClearPasswordResetRequired = () =>
    useCompanyUserStore((state) => state.clearPasswordResetRequired);
